import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

export default function FAQ() {
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    const faqs = [
        {
            category: 'Getting Started',
            questions: [
                {
                    question: 'How do I sign up on Mojo Money APP?',
                    answer: 'Download the Mojo Money app from the App Store or Google Play Store. Click "Sign Up" and provide your name, email, phone number, and create a secure password. Verify your email and phone number through the verification codes sent to you. Complete your profile by adding a payment method and ID verification for security purposes.'
                },
                {
                    question: 'What documents do I need to register?',
                    answer: 'You will need a valid government-issued ID (passport, driver\'s license, or national ID), proof of address (utility bill or bank statement), and a working email address and phone number for verification.'
                }
            ]
        },
        {
            category: 'Sending Money',
            questions: [
                {
                    question: 'How do I send money?',
                    answer: 'Open the Mojo Money app and log in. Select "Send Money" from the home screen. Choose the recipient (or add a new recipient). Enter the amount you want to send. Select the delivery method (bank deposit or cash pickup). Review the transaction details including exchange rate and fees. Confirm and complete the payment. You\'ll receive a confirmation with a tracking number.'
                },
                {
                    question: 'How long does it take for money to arrive?',
                    answer: 'Most transfers are completed within minutes for cash pickup. Bank deposits typically take 1-2 business days depending on the receiving bank. You can track your transfer status in real-time through the app.'
                },
                {
                    question: 'What are the transfer fees?',
                    answer: 'Our fees vary based on the transfer amount and delivery method. You\'ll see the exact fee before confirming any transaction. We pride ourselves on transparent pricing with no hidden charges.'
                },
                {
                    question: 'What is the maximum amount I can send?',
                    answer: 'Transfer limits depend on your verification level. Standard accounts can send up to $2,500 per transaction and $10,000 per month. Verified accounts with enhanced verification can send up to $10,000 per transaction and $50,000 per month.'
                }
            ]
        },
        {
            category: 'Bill Payments',
            questions: [
                {
                    question: 'How to pay bills or top-up?',
                    answer: 'Select "Bill Payment" or "Mobile Top-up" from the app menu. Choose the service provider (electric, water, phone, internet, etc.). Enter the account number or phone number. Enter the amount you wish to pay. Review and confirm the payment. You\'ll receive instant confirmation once the payment is processed.'
                },
                {
                    question: 'Which service providers are supported?',
                    answer: 'We support all major utility providers in Ethiopia including Ethio Telecom, Ethiopian Electric Power, Addis Ababa Water and Sewerage Authority, and major internet service providers. We\'re constantly adding new partners.'
                }
            ]
        },
        {
            category: 'Becoming a Merchant',
            questions: [
                {
                    question: 'How to become a merchant?',
                    answer: 'Visit our website and click "Become a Merchant" or contact us directly. Fill out the merchant application form with your business details. Provide required documents (business license, tax ID, bank information). Our team will review your application within 3-5 business days. Once approved, you\'ll receive training on using our merchant platform. Start serving customers and earning commissions on every transaction.'
                },
                {
                    question: 'What are the requirements to be a merchant?',
                    answer: 'You need a registered business with a valid business license, a physical storefront or office location, a bank account for settlement, reliable internet connection, and ability to maintain sufficient cash reserves for customer payouts.'
                },
                {
                    question: 'How much commission do merchants earn?',
                    answer: 'Merchants earn competitive commissions on every transaction processed. Commission rates vary based on transaction volume and type. Contact us for detailed commission structure and volume-based incentives.'
                }
            ]
        },
        {
            category: 'Security & Support',
            questions: [
                {
                    question: 'Is Mojo Money secure?',
                    answer: 'Yes, we use bank-level 256-bit encryption to protect your data and transactions. We are fully licensed and regulated by financial authorities. All transactions are monitored by advanced fraud detection systems. Your personal information is never shared with third parties without your consent.'
                },
                {
                    question: 'What if I have a problem with my transfer?',
                    answer: 'Contact our 24/7 customer support team through the app, phone, or email. Provide your transaction reference number. Our team will investigate and resolve your issue promptly. Most issues are resolved within 24 hours.'
                },
                {
                    question: 'Can I cancel a transfer?',
                    answer: 'Transfers can only be cancelled before they are processed. Contact customer support immediately if you need to cancel. If the money has already been disbursed to the recipient, the transfer cannot be cancelled, but we can help you work with the recipient for a return.'
                }
            ]
        }
    ];

    return (
        <div className="min-h-screen pt-24 pb-20">
            <section className="bg-gradient-to-br from-emerald-800 to-emerald-900 text-white py-20">
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center">
                        <h1 className="text-5xl font-bold mb-6">
                            Frequently Asked Questions
                        </h1>
                        <p className="text-xl text-emerald-50">
                            Find answers to common questions about using Mojo Money services.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-12 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        {faqs.map((category, categoryIndex) => (
                            <div key={categoryIndex} className="mb-12">
                                <h2 className="text-3xl font-bold text-emerald-900 mb-6">{category.category}</h2>
                                <div className="space-y-4">
                                    {category.questions.map((faq, questionIndex) => {
                                        const globalIndex = categoryIndex * 100 + questionIndex;
                                        const isOpen = openIndex === globalIndex;

                                        return (
                                            <div
                                                key={questionIndex}
                                                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition"
                                            >
                                                <button
                                                    onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                                                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition"
                                                >
                                                    <span className="text-lg font-semibold text-emerald-900 pr-4">
                                                        {faq.question}
                                                    </span>
                                                    {isOpen ? (
                                                        <ChevronUp className="w-6 h-6 text-emerald-800 flex-shrink-0" />
                                                    ) : (
                                                        <ChevronDown className="w-6 h-6 text-emerald-800 flex-shrink-0" />
                                                    )}
                                                </button>

                                                {isOpen && (
                                                    <div className="px-6 pb-5">
                                                        <div className="pt-4 border-t border-gray-200">
                                                            <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                                                                {faq.answer}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-12 bg-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-3xl font-bold text-emerald-900 mb-6">
                            Still Have Questions?
                        </h2>
                        <p className="text-lg text-gray-600 mb-8">
                            Our customer support team is available 24/7 to help you with any questions or concerns.
                        </p>
                        <a
                            href="/contact"
                            className="inline-block bg-yellow-500 text-emerald-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-400 transition transform hover:scale-105"
                        >
                            Contact Support
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
